// Settings.js
import React, { useState, useEffect } from 'react';
import API from '../api';
function Settings() {
  const [profile, setProfile] = useState({
    login: '',
    email: '',
    nom: '',
    prenom: '',
    entreprise: '',
    telephone: '',
    licenseKey: '',
    departementsAutorises: [],
    credits: 0,
    licenseValidUntil: '',
    activite: ''
  });
  const [subscription, setSubscription] = useState(null);
  const [stripeLoading, setStripeLoading] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  useEffect(() => {
    API.get('/api/auth/user-info')
      .then((res) => {
        const userData = res.data;
        // S'assurer que les départements autorisés sont correctement formatés
        if (userData.departementsAutorises && Array.isArray(userData.departementsAutorises)) {
          userData.departementsAutorises = userData.departementsAutorises.map(dep => dep.toString());
        } else if (userData.departementsAutorises) {
          userData.departementsAutorises = [userData.departementsAutorises.toString()];
        } else {
          userData.departementsAutorises = [];
        }
        setProfile(userData);
      })
      .catch((err) => {
        setError(err.response?.data?.detail || "Erreur lors du chargement du profil.");
      });
    API.get('/api/stripe/subscription')
      .then((res) => setSubscription(res.data))
      .catch(() => {});
  }, []);
  const validatePassword = (password, confirmPassword) => {
    const regex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\W_]).{8,}$/;
    return regex.test(password) && password === confirmPassword;
  };
  const handlePasswordUpdate = async (e) => {
    e.preventDefault();
    setMessage('');
    setError('');
    if (!validatePassword(newPassword, confirmNewPassword)) {
      setError("Le nouveau mot de passe doit comporter au moins 8 caractères, contenir une majuscule, une minuscule, un chiffre, un caractère spécial, et être identique à sa confirmation.");
      return;
    }
    try {
      const response = await API.put('/api/auth/update-password', {
        currentPassword,
        newPassword
      });
      setMessage(response.data.detail || "Mot de passe mis à jour avec succès.");
      setCurrentPassword('');
      setNewPassword('');
      setConfirmNewPassword('');
    } catch (err) {
      setError(err.response?.data?.detail || "Erreur lors de la mise à jour du mot de passe.");
    }
  };
  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    setMessage('');
    setError('');
    try {
      // Only send the modifiable field instead of the entire profile
      const response = await API.put('/api/auth/update-profile', {
        activite: profile.activite
      });
      setMessage(response.data.detail || "Profil mis à jour avec succès.");
    } catch (err) {
      setError(err.response?.data?.detail || "Erreur lors de la mise à jour du profil.");
    }
  };
  const handleRateApp = () => {
    window.open("https://votre-url-de-note-app.com", "_blank");
  };
  const handleCheckout = async (plan, addon = false) => {
    setStripeLoading(true);
    setError('');
    try {
      const res = await API.post('/api/stripe/checkout-session', { plan, addon });
      window.location.href = res.data.checkout_url;
    } catch (err) {
      setError(err.response?.data?.detail || "Erreur lors de l'initialisation du paiement.");
      setStripeLoading(false);
    }
  };
  const handlePortal = async () => {
    setStripeLoading(true);
    setError('');
    try {
      const res = await API.post('/api/stripe/portal');
      window.location.href = res.data.portal_url;
    } catch (err) {
      setError(err.response?.data?.detail || "Erreur lors de l'ouverture du portail de facturation.");
      setStripeLoading(false);
    }
  };
  const handleCancel = async () => {
    if (!window.confirm("Voulez-vous vraiment annuler votre abonnement ? Il restera actif jusqu'à la fin de la période en cours.")) return;
    setStripeLoading(true);
    setError('');
    try {
      await API.post('/api/stripe/cancel');
      setMessage("Votre abonnement sera annulé à la fin de la période en cours.");
      const res = await API.get('/api/stripe/subscription');
      setSubscription(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || "Erreur lors de l'annulation.");
    } finally {
      setStripeLoading(false);
    }
  };
  return (
    <div className="settings-container">
      <h2>Paramètres</h2>
      {error && <p className="error">{error}</p>}
      {message && <p className="success">{message}</p>}
      <section className="profile-section">
        <h3>Informations de Profil</h3>
        <form onSubmit={handleProfileUpdate}>
          <div className="form-group">
            <label>Nom et Prénom :</label>
            <div className="readonly-field">{profile.nom} {profile.prenom}</div>
          </div>
          <div className="form-group">
            <label>Entreprise :</label>
            <div className="readonly-field">{profile.entreprise}</div>
          </div>
          <div className="form-group">
            <label>Email et Téléphone :</label>
            <div className="readonly-field">{profile.email} {profile.telephone}</div>
          </div>
          <div className="form-group">
            <label>Activité :</label>
            <input
              type="text"
              value={profile.activite}
              onChange={(e) => setProfile({ ...profile, activite: e.target.value })}
              required
            />
          </div>
          <button type="submit" className="action-button">
            Mettre à jour le profil
          </button>
        </form>
      </section>
      <section className="password-section">
        <h3>Modifier le mot de passe</h3>
        <form onSubmit={handlePasswordUpdate}>
          <div className="form-group">
            <label>Mot de passe actuel :</label>
            <input
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              required
            />
          </div>
          <div className="form-group">
            <label>Nouveau mot de passe :</label>
            <input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
            />
          </div>
          <div className="form-group">
            <label>Confirmer le nouveau mot de passe :</label>
            <input
              type="password"
              value={confirmNewPassword}
              onChange={(e) => setConfirmNewPassword(e.target.value)}
              required
            />
          </div>
          <button type="submit" className="action-button">
            Mettre à jour le mot de passe
          </button>
        </form>
      </section>
      <section className="license-section">
        <h3>Licence</h3>
        <p>
          <strong>Licence :</strong> {profile.licenseKey} <br />
          <strong>Valable jusqu'au :</strong> {profile.licenseValidUntil ? new Date(profile.licenseValidUntil).toLocaleDateString('fr-FR') : 'Non définie'}
        </p>
        <p>
          <strong>Départements autorisés :</strong> {profile.departementsAutorises && profile.departementsAutorises.length > 0 
            ? profile.departementsAutorises.join(', ') 
            : 'Aucun département autorisé'}
        </p>
      </section>
      <section className="subscription-section">
        <h3>Abonnement &amp; Facturation</h3>
        {subscription && subscription.plan ? (
          <div className="current-plan-info">
            <p>
              <strong>Plan actuel :</strong>{' '}
              {subscription.plan.charAt(0).toUpperCase() + subscription.plan.slice(1)}
              {' '}—{' '}
              <span className={`plan-status plan-status--${subscription.status}`}>
                {subscription.status === 'active' ? 'Actif' :
                 subscription.status === 'cancel_at_period_end' ? 'Annulation en cours' :
                 subscription.status === 'past_due' ? 'Paiement en retard' :
                 subscription.status}
              </span>
            </p>
            {subscription.current_period_end && (
              <p>
                <strong>Renouvellement :</strong>{' '}
                {new Date(subscription.current_period_end).toLocaleDateString('fr-FR')}
              </p>
            )}
            <p>
              <strong>Extractions :</strong> {subscription.monthly_extraction_limit.toLocaleString('fr-FR')} / mois
            </p>
            <div className="subscription-actions">
              <button className="action-button" onClick={handlePortal} disabled={stripeLoading}>
                {stripeLoading ? 'Chargement...' : 'Gérer mon abonnement'}
              </button>
              {subscription.status === 'active' && (
                <button className="action-button action-button--danger" onClick={handleCancel} disabled={stripeLoading}>
                  Annuler l'abonnement
                </button>
              )}
            </div>
          </div>
        ) : (
          <p className="no-subscription">Aucun abonnement actif. Choisissez un plan ci-dessous.</p>
        )}
        <h4>Choisir un plan</h4>
        <div className="plans-grid">
          {(subscription?.available_plans || [
            { id: 'starter', label: 'ProspXion Starter', amount_eur: 39, monthly_extraction_limit: 2000, monthly_enrichment_credits: 500 },
            { id: 'pro', label: 'ProspXion Pro', amount_eur: 59, monthly_extraction_limit: 5000, monthly_enrichment_credits: 1000 },
            { id: 'enterprise', label: 'ProspXion Entreprise', amount_eur: 79, monthly_extraction_limit: 10000, monthly_enrichment_credits: 2500 }
          ]).map((plan) => (
            <div key={plan.id} className={`plan-card${subscription?.plan === plan.id ? ' plan-card--current' : ''}`}>
              <h5 className="plan-card__name">{plan.label}</h5>
              <p className="plan-card__price">{plan.amount_eur}€ <span>/mois</span></p>
              <ul className="plan-card__features">
                <li>{plan.monthly_extraction_limit.toLocaleString('fr-FR')} extractions / mois</li>
                <li>{plan.monthly_enrichment_credits.toLocaleString('fr-FR')} crédits enrichissement</li>
              </ul>
              <button
                className={`action-button${subscription?.plan === plan.id ? ' action-button--current' : ''}`}
                onClick={() => handleCheckout(plan.id)}
                disabled={stripeLoading || subscription?.plan === plan.id}
              >
                {subscription?.plan === plan.id ? 'Plan actuel' : 'Choisir ce plan'}
              </button>
            </div>
          ))}
        </div>
        <div className="addon-card">
          <h5>Pack Crédits Enrichissement</h5>
          <p>1 000 crédits supplémentaires pour l'enrichissement (email, téléphone, web)</p>
          <p className="plan-card__price">15€ <span>paiement unique</span></p>
          <button className="action-button" onClick={() => handleCheckout(null, true)} disabled={stripeLoading}>
            {stripeLoading ? 'Chargement...' : 'Acheter 1 000 crédits'}
          </button>
        </div>
      </section>
      <section className="rate-section">
        <button className="action-button" onClick={handleRateApp}>
          Noter l'application
        </button>
      </section>
    </div>
  );
}
export default Settings;
